
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInVariants } from '@/lib/animations';
import AchievementDashboard from '@/components/dashboard/AchievementDashboard';
import GratitudeJournal from '@/components/gratitude/GratitudeJournal';
import { useIsMobile } from '@/hooks/use-mobile';
import PageSEO from '@/components/seo/PageSEO';
import { cn } from '@/lib/utils';

const Home = () => {
  const isMobile = useIsMobile();

  // Structured data for organization
  const organizationData = {
    name: 'MetroCalma',
    url: 'https://metrocalma.org',
    logo: 'https://metrocalma.org/logo.png',
    description: 'Plataforma de saúde mental e bem-estar emocional com foco em terapia, meditação e testes de personalidade.',
    sameAs: [
      'https://twitter.com/metrocalma',
      'https://facebook.com/metrocalma',
      'https://instagram.com/metrocalma'
    ]
  };

  // Breadcrumbs data
  const breadcrumbs = [
    {
      position: 1,
      name: "Início",
      item: "https://metrocalma.org/app"
    }
  ];

  return (
    <MainLayout>
      <PageSEO 
        title="MetroCalma"
        description="Gerencie seu bem-estar emocional, acompanhe seu progresso e acesse ferramentas para melhorar sua saúde mental."
        canonicalUrl="https://metrocalma.org/app"
        pageName="Dashboard"
        breadcrumbs={breadcrumbs}
        structuredData={organizationData}
      />
      
      <div className={cn(
        "space-y-4",
        isMobile ? "space-y-5" : "sm:space-y-6"
      )}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className={cn(
            "font-bold",
            isMobile ? "text-2xl" : "text-xl sm:text-2xl md:text-3xl"
          )}>Olá!</h1>
          <GratitudeJournal />
        </div>

        <motion.section 
          className="grid gap-4 md:gap-6 md:grid-cols-2"
          variants={fadeInVariants}
          initial="initial"
          animate="animate"
        >
          <AchievementDashboard />

          <Card className="overflow-hidden">
            <CardHeader className={cn(
              "pb-2",
              isMobile && "pt-4 px-4"
            )}>
              <CardTitle className={isMobile ? "text-lg" : undefined}>Dica Inteligente</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "px-4 pb-4" : undefined}>
              <p className={cn(
                "text-gray-600",
                isMobile ? "text-base" : "text-sm sm:text-base"
              )}>
                Tardes ensolaradas são perfeitas para um exercício de respiração ao ar livre. 
                Reserve 5 minutos para conectar-se com a natureza.
              </p>
              <Button 
                variant="link" 
                className={cn(
                  "p-0 mt-2 h-auto text-brand-blue",
                  isMobile && "text-base min-h-[44px] -ml-3 px-3 py-2"
                )}
              >
                Iniciar exercício de respiração
              </Button>
            </CardContent>
          </Card>
        </motion.section>

        <motion.section 
          className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
          variants={fadeInVariants}
          initial="initial"
          animate="animate"
          custom={1}
        >
          <Card className="hover:shadow-md transition-all">
            <CardHeader className={cn(
              "pb-2",
              isMobile && "pt-4 px-4"
            )}>
              <CardTitle className="text-lg">Análise Avançada</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "px-4 pb-4" : undefined}>
              <p className={cn(
                "text-gray-600 mb-4",
                isMobile ? "text-base" : "text-sm sm:text-base"
              )}>
                Acalme a mente com nossas meditações guiadas
              </p>
              <Link to="/meditacoes">
                <Button className={cn(
                  "w-full",
                  isMobile ? "h-14 text-base" : "h-10"
                )}>Explorar</Button>
              </Link>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-all">
            <CardHeader className={cn(
              "pb-2",
              isMobile && "pt-4 px-4"
            )}>
              <CardTitle className="text-lg">Comunidade</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "px-4 pb-4" : undefined}>
              <p className={cn(
                "text-gray-600 mb-4",
                isMobile ? "text-base" : "text-sm sm:text-base"
              )}>
                Conecte-se com outros na jornada de bem-estar
              </p>
              <Link to="/community">
                <Button className={cn(
                  "w-full",
                  isMobile ? "h-14 text-base" : "h-10"
                )}>Participar</Button>
              </Link>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-md transition-all sm:col-span-2 md:col-span-1">
            <CardHeader className={cn(
              "pb-2",
              isMobile && "pt-4 px-4"
            )}>
              <CardTitle className="text-lg">Jornadas</CardTitle>
            </CardHeader>
            <CardContent className={isMobile ? "px-4 pb-4" : undefined}>
              <p className={cn(
                "text-gray-600 mb-4",
                isMobile ? "text-base" : "text-sm sm:text-base"
              )}>
                Participe de programas guiados para seu bem-estar
              </p>
              <Link to="/jornadas">
                <Button className={cn(
                  "w-full",
                  isMobile ? "h-14 text-base" : "h-10"
                )}>Iniciar</Button>
              </Link>
            </CardContent>
          </Card>
        </motion.section>
      </div>
    </MainLayout>
  );
};

export default Home;
